import Cookies from 'js-cookie';
import React, { createContext, useState, useEffect, useRef } from 'react';

import { ACCESS_TOKEN_COOKIE_NAME } from '~/common/constants/auth';
import { MobileNumberDialog } from '~/components/AuthenticationProvider/MobileNumberDialog/MobileNumberDialog';
import { UserApi } from '~/services/user/UserApi';

import { AuthDialog } from './AuthDialog/AuthDialog';
import { EmailLoginDialog } from './EmailLoginDialog/EmailLoginDialog';
import { EmailSignUpDialog } from './EmailSignUpDialog/EmailSignUpDialog';
import { LoginRequiredDialog } from './LoginRequiredDialog/LoginRequiredDialog';
import { identify, trackEvent } from '../analytics/analytics';
import { LOGIN_MODAL_VIEWED_EVENT_NAME } from '../analytics/constants';

import type { ReactElement, ReactNode } from 'react';
import type { UserDetails } from '~/common/types/user';

type Props = {
  children: ReactNode;
  route: string;
  userInfo: {
    numberOfUnreadMessages: number | null;
    userDetails: UserDetails | null;
  };
};

export type Context = {
  getUserDetails: () => Promise<void>;
  isDialogOpen: boolean;
  isMobileNumberDialogOpen: boolean;
  isUserDetailsLoading: boolean;
  isUserLoggedIn: boolean | null;
  isUserSitter: boolean;
  numberOfUnreadMessages: number | null;
  openAuthDialog: (title: string, successRedirectUrl?: string, queryParam?: string) => void;
  openAuthDialogWithCallback: (prop: {
    loginSuccessCallback?: () => void;
    shouldValidatePhone?: boolean;
    signupSuccessCallback?: () => void;
    title: string;
    validatePhoneSuccessCallback?: (userId: number) => void;
  }) => void;
  openEmailSignUpDialog: () => void;
  openLoginRequiredDialog: (title: string) => void;
  promo: string;
  setEmail: (email: string) => void;
  setEmailLoginDialogVisible: (isVisible: boolean) => void;
  setIsMobileNumberDialogOpen: (isOpen: boolean) => void;
  setIsUserLoggedIn(isLoggedIn: boolean): void;
  user: UserDetails | null;
};

export const AuthenticationContext = createContext<Context>({
  isDialogOpen: false,
  openAuthDialog: () => {},
  openAuthDialogWithCallback: () => {},
  openLoginRequiredDialog: () => {},
  setEmail: () => {},
  setEmailLoginDialogVisible: () => {},
  openEmailSignUpDialog: () => {},
  isUserDetailsLoading: true,
  isUserSitter: false,
  numberOfUnreadMessages: null,
  user: null,
  getUserDetails: async () => {},
  isUserLoggedIn: false,
  setIsUserLoggedIn: () => {},
  promo: '',
  setIsMobileNumberDialogOpen: () => {},
  isMobileNumberDialogOpen: false,
});

export const AuthenticationProvider = ({
  children,
  userInfo: { userDetails, numberOfUnreadMessages },
}: Props): ReactElement => {
  const [title, setTitle] = useState('');
  const [promoQueryParam, setPromoQueryParam] = useState('');
  const [validatePhone, setValidatePhone] = useState(false);
  const [onLoginSuccessRedirectUrl, setOnLoginSuccessRedirectUrl] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoginRequiredDialogOpen, setIsLoginRequiredDialogOpen] = useState(false);
  const [isEmailSignUpDialogOpen, setIsEmailSignUpDialogOpen] = useState(false);
  const [isMobileNumberDialogOpen, setIsMobileNumberDialogOpen] = useState(false);

  const [email, setEmail] = useState<string>('');
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<UserDetails | null>(userDetails);

  const loginSuccessCallbackRef = useRef<(() => void) | null>(null);
  const signupSuccessCallbackRef = useRef<(() => void) | null>(null);
  const validatePhoneSuccessCallbackRef = useRef<((userID: number) => void) | undefined>();

  useEffect(() => {
    // initialize isUserLoggedIn
    const isAuthCookiesProvided = !!Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
    setIsUserLoggedIn(isAuthCookiesProvided);
  }, []);

  useEffect(() => {
    if (user) {
      identify(user.id, { isSitter: user.isSitter });
    }
  }, [user]);

  const getUserDetails = async (): Promise<void> => {
    const userRefresh = await UserApi.getUserDetails();
    setUser(userRefresh);
  };

  const openAuthDialog = (
    newTitle: string,
    successRedirectUrl?: string,
    queryParam?: string
  ): void => {
    trackEvent(LOGIN_MODAL_VIEWED_EVENT_NAME);
    setIsDialogOpen(true);
    setTitle(newTitle);
    setOnLoginSuccessRedirectUrl(successRedirectUrl ?? '');
    setPromoQueryParam(queryParam ?? '');
  };

  const openAuthDialogWithCallback = ({
    title: newTitle,
    loginSuccessCallback,
    signupSuccessCallback,
    shouldValidatePhone,
    validatePhoneSuccessCallback,
  }: {
    loginSuccessCallback?: () => void;
    shouldValidatePhone?: boolean;
    signupSuccessCallback?: () => void;
    title: string;
    validatePhoneSuccessCallback?: (userID: number) => void;
  }): void => {
    trackEvent(LOGIN_MODAL_VIEWED_EVENT_NAME);
    setIsDialogOpen(true);
    setTitle(newTitle);
    loginSuccessCallbackRef.current = loginSuccessCallback ?? null;
    signupSuccessCallbackRef.current = signupSuccessCallback ?? null;
    if (shouldValidatePhone) {
      validatePhoneSuccessCallbackRef.current = validatePhoneSuccessCallback;
    }
    setValidatePhone(shouldValidatePhone ?? false);
  };

  const openLoginRequiredDialog = (newTitle: string): void => {
    trackEvent(LOGIN_MODAL_VIEWED_EVENT_NAME);
    setIsLoginRequiredDialogOpen(true);
    setTitle(newTitle);
  };

  const closeAuthDialog = (): void => {
    setIsDialogOpen(false);
  };

  const closeLoginRequiredDialog = (): void => {
    setIsLoginRequiredDialogOpen(false);
  };

  const [isEmailLoginDialogVisible, setEmailLoginDialogVisible] = useState<boolean>(false);

  const onEmailLoginDialogClose = (): void => {
    setEmailLoginDialogVisible(false);
  };

  const handleEmailSignUpDialogClose = (): void => {
    setIsEmailSignUpDialogOpen(false);
  };

  const openEmailSignUpDialog = (): void => {
    setIsEmailSignUpDialogOpen(true);
  };

  const handleSignUpSuccessCallback = (): void => {
    signupSuccessCallbackRef.current?.();

    if (validatePhone) {
      setIsMobileNumberDialogOpen(true);
    }
  };

  return (
    <AuthenticationContext.Provider
      value={{
        isDialogOpen,
        openAuthDialog,
        openAuthDialogWithCallback,
        openLoginRequiredDialog,
        setEmail,
        setEmailLoginDialogVisible,
        setIsMobileNumberDialogOpen,
        openEmailSignUpDialog,
        isUserDetailsLoading: false,
        isUserSitter: user?.isSitter ?? false,
        user,
        numberOfUnreadMessages,
        getUserDetails,
        isUserLoggedIn,
        setIsUserLoggedIn,
        promo: promoQueryParam,
        isMobileNumberDialogOpen,
      }}
    >
      <>
        {children}
        <AuthDialog
          handleDialogClose={closeAuthDialog}
          handleLoginSuccessCallback={loginSuccessCallbackRef.current}
          handleSocialSignUpSuccessCallback={handleSignUpSuccessCallback}
          isOpen={isDialogOpen}
          onLoginSuccessRedirectUrl={onLoginSuccessRedirectUrl}
          promoQueryParam={promoQueryParam}
          title={title}
        />

        <LoginRequiredDialog
          isOpen={isLoginRequiredDialogOpen}
          onDialogClose={closeLoginRequiredDialog}
          promoQueryParam={promoQueryParam}
          title={title}
        />

        <EmailLoginDialog
          email={email}
          handleEmailLoginDialogClose={onEmailLoginDialogClose}
          handleLoginSuccessCallback={loginSuccessCallbackRef.current}
          isOpen={isEmailLoginDialogVisible}
        />

        <EmailSignUpDialog
          email={email}
          handleEmailSignUpDialogClose={handleEmailSignUpDialogClose}
          handleSignUpSuccessCallback={handleSignUpSuccessCallback}
          isOpen={isEmailSignUpDialogOpen}
        />

        <MobileNumberDialog handleValidatePhoneSuccess={validatePhoneSuccessCallbackRef.current} />
      </>
    </AuthenticationContext.Provider>
  );
};
